import { useMemo, useState } from 'react';
import type { Alarm } from '@bridgewerk/shared';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { Button } from '../../../components/ui/Button.js';
import { Badge } from '../../../components/ui/Badge.js';

const PAGE_SIZE = 20;

type SortKey = 'first_seen_at' | 'last_seen_at' | 'alarm_code' | 'cleared_at';
type SortDir = 'asc' | 'desc';

interface SortState {
  readonly key: SortKey;
  readonly dir: SortDir;
}

export interface AlarmHistoryTableProps {
  readonly alarms: Alarm[] | undefined;
  readonly loading?: boolean;
}

export function AlarmHistoryTable({ alarms, loading }: AlarmHistoryTableProps): JSX.Element {
  const [sort, setSort] = useState<SortState>({ key: 'first_seen_at', dir: 'desc' });
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => sortAlarms(alarms ?? [], sort), [alarms, sort]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const toggleSort = (key: SortKey): void => {
    setSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
    setPage(0);
  };

  return (
    <section className="rounded-md border border-border bg-card overflow-hidden">
      <header className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">
          Alarm history
        </h2>
        <Button
          variant="secondary"
          onClick={() => downloadCsv(sorted)}
          disabled={!sorted.length}
        >
          Export CSV
        </Button>
      </header>
      {loading && !alarms ? (
        <div className="p-4 space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-8" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div className="p-8 text-center text-sm text-text-dim">
          No alarms recorded for this machine. Quiet floors are productive floors.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim border-b border-border">
                  <Th label="First seen" sortKey="first_seen_at" sort={sort} onSort={toggleSort} />
                  <Th label="Code" sortKey="alarm_code" sort={sort} onSort={toggleSort} />
                  <th className="px-4 py-2 font-normal">Description</th>
                  <Th label="Last seen" sortKey="last_seen_at" sort={sort} onSort={toggleSort} />
                  <Th label="Cleared" sortKey="cleared_at" sort={sort} onSort={toggleSort} />
                  <th className="px-4 py-2 font-normal">Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((a) => {
                  const cleared = a.cleared_at != null;
                  return (
                    <tr
                      key={`${a.alarm_code}-${a.first_seen_at}`}
                      className="border-t border-border"
                      style={{
                        borderLeft: `3px solid ${cleared ? 'transparent' : 'var(--color-red)'}`,
                      }}
                    >
                      <td className="px-4 py-2 font-mono text-xs text-text-dim">
                        {formatDateTime(a.first_seen_at)}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-text">{a.alarm_code}</td>
                      <td className="px-4 py-2 text-xs">
                        {a.program_name ? (
                          <>
                            during <span className="font-mono text-text">{a.program_name}</span>
                          </>
                        ) : (
                          <span className="text-text-dim">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-text-dim">
                        {formatDateTime(a.last_seen_at)}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-text-dim">
                        {a.cleared_at ? formatDuration(a.first_seen_at, a.cleared_at) : '—'}
                      </td>
                      <td className="px-4 py-2">
                        <Badge tone={cleared ? 'opportunity' : 'critical'}>
                          {cleared ? 'Resolved' : 'Active'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageCount > 1 && (
            <footer className="px-4 py-2 border-t border-border flex items-center justify-between">
              <span className="text-[11px] font-mono text-text-dim">
                Page {page + 1} of {pageCount} — {sorted.length} alarms total
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  ← Prev
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1}
                >
                  Next →
                </Button>
              </div>
            </footer>
          )}
        </>
      )}
    </section>
  );
}

function Th({
  label,
  sortKey,
  sort,
  onSort,
}: {
  readonly label: string;
  readonly sortKey: SortKey;
  readonly sort: SortState;
  readonly onSort: (key: SortKey) => void;
}): JSX.Element {
  const active = sort.key === sortKey;
  const arrow = active ? (sort.dir === 'desc' ? ' ▼' : ' ▲') : '';
  return (
    <th className="px-4 py-2 font-normal">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`hover:text-text ${active ? 'text-text' : ''}`}
      >
        {label}
        {arrow}
      </button>
    </th>
  );
}

function sortAlarms(rows: Alarm[], sort: SortState): Alarm[] {
  const out = [...rows];
  out.sort((a, b) => {
    const av = a[sort.key];
    const bv = b[sort.key];
    if (av == null && bv == null) return 0;
    if (av == null) return sort.dir === 'desc' ? 1 : -1;
    if (bv == null) return sort.dir === 'desc' ? -1 : 1;
    if (av < bv) return sort.dir === 'desc' ? 1 : -1;
    if (av > bv) return sort.dir === 'desc' ? -1 : 1;
    return 0;
  });
  return out;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatDuration(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.round(hr / 24);
  return `${day}d`;
}

function downloadCsv(alarms: Alarm[]): void {
  const header = ['shop_id', 'machine_id', 'alarm_code', 'program_name', 'first_seen_at', 'last_seen_at', 'cleared_at'];
  const lines = [
    header.join(','),
    ...alarms.map((a) =>
      [
        a.shop_id,
        a.machine_id,
        a.alarm_code,
        a.program_name ?? '',
        a.first_seen_at,
        a.last_seen_at,
        a.cleared_at ?? '',
      ]
        .map(csvEscape)
        .join(','),
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `alarms-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function csvEscape(value: string): string {
  if (value == null) return '';
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
